import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import WritingEditorPage from './WritingEditorPage'
import { generateInitialContent } from './writingUtils'
import { MODEL_MODES } from "../../LLM/index.js"
import { AgentHistoryIntegration } from '../../utils/agentHistoryIntegration'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import CreditWarningModal from '../../components/CreditWarningModal'
import { useSubscription } from '../../hooks/useSubscription'
import { AGENT_TYPES } from '../index.js'

const WritingAgent = ({ query, onBack, llmService }) => {
  // Check URL parameters for continue editing mode
  const urlParams = new URLSearchParams(window.location.search)
  const isContinueMode = urlParams.get('continue') === 'true' || 
                        urlParams.has('continueQuery') || 
                        urlParams.has('existingTitle') || 
                        urlParams.has('existingContent')
  
  // Get existing content from sessionStorage first, then fallback to URL parameters
  let existingTitle = ''
  let existingContent = ''
  let continueQuery = query
  
  if (isContinueMode) {
    console.log('🔄 Continue editing mode detected')
    
    try {
      // Try to load from sessionStorage first
      const writingContinueData = sessionStorage.getItem('writingContinueData')
      console.log('📦 SessionStorage data found:', !!writingContinueData)
      
      if (writingContinueData) {
        const data = JSON.parse(writingContinueData)
        continueQuery = data.query || query
        existingTitle = data.title || ''
        existingContent = data.content || ''
        
        console.log('📥 Loaded from sessionStorage:', {
          titleLength: existingTitle.length,
          contentLength: existingContent.length,
          query: continueQuery,
          hasTitle: !!existingTitle,
          hasContent: !!existingContent
        })
        
        // Don't clear sessionStorage immediately, let useEffect handle it
        // sessionStorage.removeItem('writingContinueData')
      } else {
        // Fallback to URL parameters if no sessionStorage data
        console.log('⚠️ No sessionStorage data, trying URL parameters')
        
        // Check for new parameter names from AgentHistoryPage
        if (urlParams.has('continueQuery') || urlParams.has('existingTitle') || urlParams.has('existingContent')) {
          continueQuery = urlParams.get('continueQuery') ? decodeURIComponent(urlParams.get('continueQuery')) : query
          existingTitle = urlParams.get('existingTitle') ? decodeURIComponent(urlParams.get('existingTitle')) : ''
          existingContent = urlParams.get('existingContent') ? decodeURIComponent(urlParams.get('existingContent')) : ''
        } else {
          // Fallback to old parameter names for backward compatibility
          existingTitle = urlParams.get('title') ? decodeURIComponent(urlParams.get('title')) : ''
          existingContent = urlParams.get('content') ? decodeURIComponent(urlParams.get('content')) : ''
        }
        
        console.log('📥 Loaded from URL params (fallback):', {
          titleLength: existingTitle.length,
          contentLength: existingContent.length,
          query: continueQuery,
          hasTitle: !!existingTitle,
          hasContent: !!existingContent
        })
      }
    } catch (error) {
      console.error('❌ Error loading continue data:', error)
      // Fallback to URL parameters
      if (urlParams.has('continueQuery') || urlParams.has('existingTitle') || urlParams.has('existingContent')) {
        continueQuery = urlParams.get('continueQuery') ? decodeURIComponent(urlParams.get('continueQuery')) : query
        existingTitle = urlParams.get('existingTitle') ? decodeURIComponent(urlParams.get('existingTitle')) : ''
        existingContent = urlParams.get('existingContent') ? decodeURIComponent(urlParams.get('existingContent')) : ''
      } else {
        existingTitle = urlParams.get('title') ? decodeURIComponent(urlParams.get('title')) : ''
        existingContent = urlParams.get('content') ? decodeURIComponent(urlParams.get('content')) : ''
      }
      
      console.log('🔄 Using URL fallback due to error:', {
        titleLength: existingTitle.length,
        contentLength: existingContent.length,
        query: continueQuery
      })
    }
  }
  
  const [documentContent, setDocumentContent] = useState(isContinueMode ? existingContent : '')
  const [documentTitle, setDocumentTitle] = useState(isContinueMode ? existingTitle : '')
  const [isLoading, setIsLoading] = useState(!isContinueMode)
  const [error, setError] = useState(null)
  const [hasInitialized, setHasInitialized] = useState(isContinueMode && (existingTitle || existingContent)) // Skip initialization if continuing with existing data
  
  // Log initial state for debugging
  console.log('🚀 WritingAgent initialized with:', {
    isContinueMode,
    existingTitle: existingTitle || 'none',
    existingContent: existingContent ? `${existingContent.length} chars` : 'none',
    documentTitle,
    documentContent: documentContent ? `${documentContent.length} chars` : 'none',
    isLoading,
    hasInitialized
  })
  const { warningModal, checkAndWarnCredits, closeWarning } = useCreditWarning()
  const { consumeCredits } = useSubscription()
  
  // Use refs to track state and prevent unnecessary re-generation
  const isGeneratingContent = useRef(false)
  const currentQuery = useRef(isContinueMode ? continueQuery : query)
  const hasEverGenerated = useRef(isContinueMode && (existingTitle || existingContent)) // Mark as generated if continuing with existing content

  // Session storage keys for persistence
  const getSessionKey = (suffix) => `writing_agent_${isContinueMode ? continueQuery : query}_${suffix}`

  // Load persisted state on component mount and auto-generate content
  useEffect(() => {
    const loadPersistedState = () => {
      try {
        const persistedContent = sessionStorage.getItem(getSessionKey('content'))
        const persistedTitle = sessionStorage.getItem(getSessionKey('title'))
        
        if (persistedContent) {
          const content = JSON.parse(persistedContent)
          const title = persistedTitle ? JSON.parse(persistedTitle) : ''
          
          console.log('📋 Loading persisted writing state')
          setDocumentContent(content)
          setDocumentTitle(title)
          setHasInitialized(true)
          hasEverGenerated.current = true
          
          return true
        }
      } catch (error) {
        console.warn('⚠️ Failed to load persisted state:', error)
        // Clear corrupted data
        try {
          sessionStorage.removeItem(getSessionKey('content'))
          sessionStorage.removeItem(getSessionKey('title'))
        } catch (clearError) {
          console.warn('⚠️ Failed to clear corrupted session data:', clearError)
        }
      }
      return false
    }
    
    const activeQuery = isContinueMode ? continueQuery : query
    
    // Check if query changed and reset if needed (only if not in continue mode)
    if (!isContinueMode && currentQuery.current !== activeQuery) {
      console.log('🔄 Query changed, resetting state...')
      setDocumentContent('')
      setDocumentTitle('')
      setHasInitialized(false)
      hasEverGenerated.current = false
      
      // Clear old session data
      try {
        const oldQuery = currentQuery.current
        sessionStorage.removeItem(`writing_agent_${oldQuery}_content`)
        sessionStorage.removeItem(`writing_agent_${oldQuery}_title`)
      } catch (error) {
        console.warn('⚠️ Failed to clear old session data:', error)
      }
      currentQuery.current = activeQuery
    } else if (currentQuery.current === activeQuery) {
      // Only load persisted state if query matches current query and not in continue mode with existing data
      if (!isContinueMode) {
        const hasPersistedState = loadPersistedState()
        if (hasPersistedState) {
          return // Skip content generation
        }
      }
    }
    
    // Auto-generate content if not already generated
    if (!hasEverGenerated.current && !isGeneratingContent.current) {
      // In continue mode, only generate if no existing content
      if (isContinueMode) {
        console.log('📝 WritingAgent in continue mode')
        if (existingTitle || existingContent) {
          console.log('✅ Found existing content, skipping generation')
          
          // Set the content and title to state
          // Note: existingContent is already in markdown format, no need to process
          setDocumentContent(existingContent)
          setDocumentTitle(existingTitle)
          setIsLoading(false)
          setHasInitialized(true)
          hasEverGenerated.current = true
          
          // Clear sessionStorage after successfully loading data
          try {
            sessionStorage.removeItem('writingContinueData')
            console.log('🧹 Cleared sessionStorage after loading')
          } catch (error) {
            console.warn('⚠️ Failed to clear sessionStorage:', error)
          }
        } else {
          console.log('⚠️ No existing content found in continue mode, generating new content')
          handleStartWriting()
        }
        // Clean up URL parameters after loading
        const newUrl = window.location.pathname
        window.history.replaceState({}, '', newUrl)
      } else {
        console.log('🚀 WritingAgent initializing with query:', activeQuery)
        handleStartWriting()
      }
    }
  }, [isContinueMode ? continueQuery : query, isContinueMode, existingTitle, existingContent])

  // Persist state changes to session storage
  useEffect(() => {
    if (hasInitialized && documentContent) {
      try {
        sessionStorage.setItem(getSessionKey('content'), JSON.stringify(documentContent))
        sessionStorage.setItem(getSessionKey('title'), JSON.stringify(documentTitle))
      } catch (error) {
        console.warn('⚠️ Failed to persist state:', error)
      }
    }
  }, [documentContent, documentTitle, hasInitialized, query])

  const handleStartWriting = async () => {
    if (isGeneratingContent.current || hasEverGenerated.current) {
      console.log('⚠️ Content generation already in progress or completed, skipping...')
      return
    }
    
    isGeneratingContent.current = true
    
    try {
      setIsLoading(true)
      setError(null)
      const activeQuery = isContinueMode ? continueQuery : query
      console.log('🔄 Generating initial content for:', activeQuery)
      
      const result = await generateInitialContent(activeQuery, llmService)
      setDocumentContent(result.content)
      setDocumentTitle(result.title)
      setHasInitialized(true)
      hasEverGenerated.current = true
      
      console.log('✅ Initial content generated successfully')
      
      // Save to agent history after successful completion (only for new documents, not continue mode)
      if (!isContinueMode) {
        try {
          const saveResult = await AgentHistoryIntegration.saveWritingResult({
            query: activeQuery,
            title: result.title,
            content: result.content
          })
          
          if (saveResult.success) {
            console.log('✅ Writing berhasil disimpan ke history:', saveResult.data)
            // Update last save reference to prevent duplicate auto-saves
            const currentData = JSON.stringify({ 
              title: result.title, 
              content: result.content 
            })
            lastSaveRef.current = currentData
          } else {
            console.warn('⚠️ Gagal menyimpan writing ke history:', saveResult.error)
          }
        } catch (saveError) {
          console.error('❌ Error saat menyimpan ke history:', saveError)
        }
      }
    } catch (error) {
      console.error('❌ Error generating initial content:', error)
      setError('Gagal membuat konten awal')
      setHasInitialized(false)
      hasEverGenerated.current = false
    } finally {
      setIsLoading(false)
      isGeneratingContent.current = false
    }
  }

  const handleContentUpdate = (newContent) => {
    setDocumentContent(newContent)
    
    // Auto-save to history when content is updated in continue mode
    if (isContinueMode && newContent.trim()) {
      debouncedSaveToHistory(newContent, documentTitle)
    }
  }

  const handleTitleUpdate = (newTitle) => {
    setDocumentTitle(newTitle)
    
    // Auto-save to history when title is updated in continue mode
    if (isContinueMode && newTitle.trim()) {
      debouncedSaveToHistory(documentContent, newTitle)
    }
  }
  
  // Debounced function to save to history (avoid too frequent saves)
  const lastSaveRef = useRef(null)
  const saveInProgressRef = useRef(false)
  
  const debouncedSaveToHistory = useCallback(
    debounce(async (content, title) => {
      if (saveInProgressRef.current) return
      
      // Prevent duplicate saves with same content
      const currentData = JSON.stringify({ title, content })
      if (lastSaveRef.current === currentData) {
        console.log('🔄 Skipping duplicate auto-save')
        return
      }
      
      try {
        saveInProgressRef.current = true
        const activeQuery = isContinueMode ? continueQuery : query
        const saveResult = await AgentHistoryIntegration.saveWritingResult({
          query: activeQuery,
          title: title || 'Dokumen Tanpa Judul',
          content: content
        })
        
        if (saveResult.success) {
          console.log('✅ Auto-saved to history:', saveResult.data)
          lastSaveRef.current = currentData
        } else {
          console.warn('⚠️ Failed to auto-save to history:', saveResult.error)
        }
      } catch (saveError) {
        console.error('❌ Error auto-saving to history:', saveError)
      } finally {
        saveInProgressRef.current = false
      }
    }, 5000), // Save after 5 seconds of inactivity
    [query, continueQuery, isContinueMode]
  )

  // Manual save function
  const manualSaveInProgressRef = useRef(false)
  
  const handleManualSave = useCallback(async (data) => {
    if (manualSaveInProgressRef.current) {
      console.log('🔄 Manual save already in progress, skipping')
      return
    }
    
    try {
      manualSaveInProgressRef.current = true
      console.log('💾 Manual save triggered with data:', data)
      
      const activeQuery = isContinueMode ? continueQuery : query
      const saveResult = await AgentHistoryIntegration.saveWritingResult({
        query: activeQuery,
        title: data.title || 'Dokumen Tanpa Judul',
        content: data.content
      })
      
      if (saveResult.success) {
        console.log('✅ Manual save successful:', saveResult.data)
        
        // Update last save reference to prevent auto-save duplication
        const currentData = JSON.stringify({ 
          title: data.title || 'Dokumen Tanpa Judul', 
          content: data.content 
        })
        lastSaveRef.current = currentData
        
        return saveResult
      } else {
        console.warn('⚠️ Manual save failed:', saveResult.error)
        throw new Error(saveResult.error || 'Gagal menyimpan dokumen')
      }
    } catch (saveError) {
      console.error('❌ Error in manual save:', saveError)
      throw saveError
    } finally {
      manualSaveInProgressRef.current = false
    }
  }, [query, continueQuery, isContinueMode])
  
  // Simple debounce function
  function debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  const handleAIAction = async (action, selectedText, context) => {
    // Check credits before AI action - use the specific action type
    const canProceed = await checkAndWarnCredits(action)
    
    if (!canProceed) {
      throw new Error('Kredit tidak mencukupi')
    }

    try {
      // Consume credits for AI action - use the specific action type
      const creditResult = await consumeCredits(action)
      if (!creditResult.success) {
        throw new Error('Gagal mengkonsumsi kredit')
      }

      // Perform AI action based on type
      const activeQuery = isContinueMode ? continueQuery : query
      const result = await performAIAction(action, selectedText, context, llmService, activeQuery)
      
      // Validate result
      if (!result || typeof result !== 'string' || result.trim().length === 0) {
        throw new Error('AI tidak mengembalikan hasil yang valid')
      }
      
      return result.trim()
    } catch (error) {
      console.error('❌ Error in AI action:', error)
      throw error
    }
  }

  // Show loading state while generating content (but not in continue mode with existing data)
  if (isLoading && !isContinueMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Writing Agent</h1>
            <p className="text-lg text-gray-600 mb-8">
              AI-powered document editor untuk membuat dan mengedit berbagai jenis teks dengan bantuan kecerdasan buatan
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-600 text-lg">Membuat konten awal untuk: <strong>{query}</strong></p>
                <p className="text-gray-500 text-sm mt-2">Mohon tunggu sebentar...</p>
              </div>
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">{error}</p>
                <button
                  onClick={handleStartWriting}
                  className="mt-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded transition-colors duration-200"
                >
                  Coba Lagi
                </button>
              </div>
            )}
          </div>

          <div className="text-center">
            <button
              onClick={onBack}
              className="text-gray-600 hover:text-gray-800 font-medium"
            >
              ← Kembali ke Agent Selector
            </button>
          </div>
        </div>

        {/* Credit Warning Modal */}
        {warningModal.show && (
          <CreditWarningModal
            isOpen={warningModal.show}
            onClose={closeWarning}
            requiredCredits={warningModal.requiredCredits}
            currentCredits={warningModal.currentCredits}
            agentType={warningModal.agentType}
          />
        )}
      </div>
    )
  }

  // Show editor when content is ready
  return (
    <WritingEditorPage
      title={documentTitle}
      content={documentContent}
      onTitleUpdate={handleTitleUpdate}
      onContentUpdate={handleContentUpdate}
      onAIAction={handleAIAction}
      onBack={onBack}
      llmService={llmService}
      documentId={isContinueMode ? continueQuery : `${query}_${Date.now()}`}
      onSave={handleManualSave}
    />
  )
}

// Helper function to perform AI actions
const performAIAction = async (action, selectedText, context, llmService, activeQuery = 'dokumen') => {
  // Validate inputs
  if (!selectedText || selectedText.trim().length === 0) {
    throw new Error('Teks yang dipilih kosong')
  }
  
  if (!llmService || typeof llmService.sendMessage !== 'function') {
    throw new Error('LLM Service tidak tersedia')
  }
  
  const prompts = {
    improve: `Perbaiki dan tingkatkan kualitas teks berikut agar lebih jelas, engaging, dan profesional. Pertahankan makna asli tetapi buat lebih baik:\n\n"${selectedText}"\n\nKonteks dokumen: ${context}\n\nTopik asli: ${activeQuery}\n\nBerikan hasil perbaikan langsung tanpa penjelasan tambahan.`,
    expand: `Kembangkan dan perluas teks berikut dengan menambahkan detail, contoh, dan penjelasan yang relevan. Buat menjadi lebih komprehensif:\n\n"${selectedText}"\n\nKonteks dokumen: ${context}\n\nTopik asli: ${activeQuery}\n\nBerikan hasil pengembangan langsung tanpa penjelasan tambahan.`,
    shorten: `Ringkas dan persingkat teks berikut sambil mempertahankan poin-poin penting dan makna utama:\n\n"${selectedText}"\n\nKonteks dokumen: ${context}\n\nTopik asli: ${activeQuery}\n\nBerikan hasil ringkasan langsung tanpa penjelasan tambahan.`,
    rephrase: `Tulis ulang teks berikut dengan gaya dan struktur kalimat yang berbeda tetapi mempertahankan makna yang sama:\n\n"${selectedText}"\n\nKonteks dokumen: ${context}\n\nTopik asli: ${activeQuery}\n\nBerikan hasil penulisan ulang langsung tanpa penjelasan tambahan.`,
    translate: `Terjemahkan teks berikut ke bahasa Inggris dengan akurat dan natural:\n\n"${selectedText}"\n\nKonteks dokumen: ${context}\n\nTopik asli: ${activeQuery}\n\nBerikan hasil terjemahan langsung tanpa penjelasan tambahan.`,
    grammar: `Perbaiki tata bahasa, ejaan, dan struktur kalimat pada teks berikut:\n\n"${selectedText}"\n\nKonteks dokumen: ${context}\n\nTopik asli: ${activeQuery}\n\nBerikan hasil koreksi langsung tanpa penjelasan tambahan.`
  }

  const prompt = prompts[action] || prompts.improve
  
  console.log(`🔄 Performing AI action: ${action} for text: "${selectedText.substring(0, 50)}..."`);

  try {
    const response = await llmService.sendMessage(
      [{ role: 'user', content: prompt }],
      { 
        mode: MODEL_MODES.STANDARD,
        maxTokens: 2000,
        stream: false,
        consumeCredits: false
      }
    )
    
    console.log(`✅ AI response received for action ${action}:`, response?.substring(0, 100) + '...');

    if (!response) {
      throw new Error('Respons AI null atau undefined')
    }
    
    if (typeof response !== 'string') {
      throw new Error(`Respons AI bukan string, tipe: ${typeof response}`)
    }
    
    const trimmedResponse = response.trim()
    if (trimmedResponse.length === 0) {
      throw new Error('Respons AI kosong setelah trim')
    }

    return trimmedResponse
  } catch (error) {
    console.error(`❌ Error in AI action ${action}:`, error)
    
    // Provide more specific error messages
    if (error.message?.includes('network') || error.message?.includes('fetch')) {
      throw new Error('Koneksi internet bermasalah, coba lagi')
    } else if (error.message?.includes('rate limit') || error.message?.includes('quota')) {
      throw new Error('Batas penggunaan API tercapai, coba lagi nanti')
    } else if (error.message?.includes('unauthorized') || error.message?.includes('authentication')) {
      throw new Error('Masalah autentikasi API, hubungi administrator')
    } else {
      throw new Error(`Gagal melakukan aksi AI: ${error.message || 'Error tidak diketahui'}`)
    }
  }
}

WritingAgent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object.isRequired
}

export default WritingAgent